import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { TPromoBannerBase } from '@onesource/schemas';
import { sanitizeRichHtml } from '@onesource/utils';
import { FC } from 'react';
import { MdChevronRight } from 'react-icons/md';

import { useGetByDevice } from '../hooks';
import { resolvePromoBannerBackground } from './promo-banner-colors';
import { getPromoBannerIconScheme } from './promo-banner-icon-scheme';
import { promoBannerRichTextSx } from './promo-banner-rich-text';

export interface PromoBannerDefaultProps {
  banner: TPromoBannerBase;
}

/**
 * Default promo banner layout.
 *
 * Coral-gradient card with a configurable icon badge (banner.iconScheme),
 * title, description and a pill CTA that links to the configured URL. Lays out
 * as a row on desktop and stacks on mobile.
 */
export const PromoBannerDefault: FC<PromoBannerDefaultProps> = ({ banner }) => {
  const isExternal = /^https?:\/\//i.test(banner.buttonUrl);
  const { isMobile } = useGetByDevice();
  const BadgeIcon = getPromoBannerIconScheme(banner.iconScheme);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 1, sm: 2 },
        p: { xs: 1, sm: 2.5 },
        borderRadius: { xs: '11px', md: '15px' },
        width: '100%',
        minHeight: { xs: '59px', md: '79px' },
        background: resolvePromoBannerBackground(banner.backgroundColor),
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          minWidth: { xs: '22px', md: '46px' },
          minHeight: { xs: '22px', md: '46px' },
          borderRadius: { xs: '6px', md: '12px' },
          bgcolor: '#FFF1EF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BadgeIcon width={isMobile ? 14 : 29} height={isMobile ? 14 : 29} />
      </Box>

      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            ...promoBannerRichTextSx,
            color: theme => theme.palette.common.white,
            fontWeight: 700,
            fontSize: { xs: 12, md: 16 },
            lineHeight: { xs: '17px', md: '22px' },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(banner.title) }}
        />
        <Box
          sx={{
            ...promoBannerRichTextSx,
            fontWeight: 400,
            color: theme => theme.palette.common.white,
            fontSize: { xs: 12, md: '14px' },
            lineHeight: {
              xs: '15px',
              md: '17px',
            },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(banner.description) }}
        />
      </Stack>

      <Button
        variant="outlined"
        href={banner.buttonUrl}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        endIcon={<MdChevronRight size={20} />}
        sx={{
          minHeight: { xs: '19px', md: '32px' },
          borderRadius: { xs: '6px', md: '8px' },
          flexShrink: 0,
          alignSelf: { xs: 'center', sm: 'center' },
          color: '#fff !important',
          borderColor: theme => theme.palette.common.white,
          fontSize: { xs: '9px', md: '12px' },
          fontWeight: 700,
          maxHeight: { xs: '19px', md: '32px' },
          pl: 1,
          whiteSpace: 'nowrap',
          '& .MuiButton-endIcon': { ml: 0.1 },
          '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255, 255, 255, 0.14)' },
        }}
      >
        {banner.buttonLabel}
      </Button>
    </Box>
  );
};
